/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  FeedbackCommentItem,
  FeedbackThreadItem,
  PDF_THREAD_LABEL_SEPARATOR,
  PdfAnnotationRef,
} from '@app/app/shared/components-v3/feedback/feedback.model';
import { FileAnswer } from '@app/app/shared/models';
import { PdfAnnotationReplyType, PdfAnnotationSubtype } from '@embedpdf/models';
import { marker } from '@jsverse/transloco-keys-manager/marker';

/** The fields read from an annotation. Dates are ISO strings once stored and Date objects while the editor holds them. */
interface CommentedAnnotation {
  id: string;
  type: PdfAnnotationSubtype;
  pageIndex: number;
  rect?: { origin: { x: number; y: number } };
  contents?: string;
  author?: string;
  created?: string | Date;
  modified?: string | Date;
  inReplyToId?: string;
  replyType?: PdfAnnotationReplyType;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

const PAGE_KEY = marker('translations.global.pagination.page');
const REMOVED_KEY = marker('translations.v3Forms.fileUpload.pdfComment.removed');
const CYCLE_KEY = marker('translations.v3Profile.folioFeedback.cycleLabel');

const TYPE_KEYS: Partial<Record<PdfAnnotationSubtype, string>> = {
  [PdfAnnotationSubtype.TEXT]: marker('translations.v3Forms.fileUpload.pdfComment.type.note'),
  [PdfAnnotationSubtype.HIGHLIGHT]: marker('translations.v3Forms.fileUpload.pdfComment.type.highlight'),
  [PdfAnnotationSubtype.UNDERLINE]: marker('translations.v3Forms.fileUpload.pdfComment.type.underline'),
  [PdfAnnotationSubtype.STRIKEOUT]: marker('translations.v3Forms.fileUpload.pdfComment.type.strikethrough'),
  [PdfAnnotationSubtype.INK]: marker('translations.v3Forms.fileUpload.pdfComment.type.drawing'),
  [PdfAnnotationSubtype.FREETEXT]: marker('translations.v3Forms.fileUpload.pdfComment.type.textBox'),
  [PdfAnnotationSubtype.LINE]: marker('translations.v3Forms.fileUpload.pdfComment.type.shape'),
  [PdfAnnotationSubtype.SQUARE]: marker('translations.v3Forms.fileUpload.pdfComment.type.shape'),
  [PdfAnnotationSubtype.CIRCLE]: marker('translations.v3Forms.fileUpload.pdfComment.type.shape'),
  [PdfAnnotationSubtype.POLYGON]: marker('translations.v3Forms.fileUpload.pdfComment.type.shape'),
  [PdfAnnotationSubtype.POLYLINE]: marker('translations.v3Forms.fileUpload.pdfComment.type.shape'),
};

/**
 * Read-only feedback threads from one PDF's annotations, the way the viewer's comment pane groups them: one per
 * annotation that has text or replies, holding its own text and then its replies. Group members (`replyType` Group)
 * belong to their group's annotation and are skipped. Threads run in page order, top to bottom. A comment whose
 * annotation is in `draftIds` is marked as a draft.
 *
 * Only for playlists: an assignment's PDF comments are feedback threads of their own.
 */
export function pdfCommentThreads(
  annotations: readonly object[],
  file: Pick<FileAnswer, 'path' | 'fileName'>,
  translate: Translate,
  draftIds: ReadonlySet<string> = new Set(),
): FeedbackThreadItem[] {
  const all = annotations as CommentedAnnotation[];
  const repliesByParent = new Map<string, CommentedAnnotation[]>();
  for (const annotation of all) {
    if (isReply(annotation) && hasText(annotation)) {
      repliesByParent.set(annotation.inReplyToId, [...(repliesByParent.get(annotation.inReplyToId) ?? []), annotation]);
    }
  }

  return all
    .filter((annotation) => !annotation.inReplyToId)
    .map((root) => ({
      root,
      comments: [...(hasText(root) ? [root] : []), ...[...(repliesByParent.get(root.id) ?? [])].sort(byCreated)],
    }))
    .filter(({ comments }) => comments.length > 0)
    .sort((a, b) => a.root.pageIndex - b.root.pageIndex || (a.root.rect?.origin.y ?? 0) - (b.root.rect?.origin.y ?? 0))
    .map(({ root, comments }): FeedbackThreadItem => {
      const pdfAnnotation = { file: file.path, annotationId: root.id, pageIndex: root.pageIndex };
      return {
        id: `pdf:${root.id}`,
        status: 'OPEN',
        label: threadLabel(pdfAnnotation, file.fileName, root, false, translate),
        cycles: [],
        commentCount: comments.length,
        comments: comments.map((comment) => toComment(comment, draftIds.has(comment.id))),
        pdfAnnotation,
        readOnly: true,
      };
    });
}

/** What a form's file answer shows, for `arrangePdfThreads`. */
export interface PdfThreadsOnShow {
  files: readonly Pick<FileAnswer, 'path' | 'fileName'>[];
  /** The annotations each file's viewer shows, by stored path. */
  marks: Readonly<Record<string, readonly object[]>>;
  /** The files whose viewer shows annotations, by stored path, with the submission cycle it shows if known. */
  viewers: ReadonlyMap<string, number | undefined>;
  /** The cycle the host shows, for a file it has no viewer for. */
  cycle?: number;
  /** The host can switch to a mark's cycle to show it there (the marking view). */
  switchesCycle?: boolean;
}

/**
 * Names a form's PDF threads and lists them after its others, in file, page and top-to-bottom order. A mark's type and
 * position are known once its file has loaded. A mark from another cycle is named by its cycle, and has no jump unless
 * the host can switch to that cycle.
 */
export function arrangePdfThreads<T extends { pdfAnnotation?: PdfAnnotationRef | null; label?: string | null }>(
  threads: readonly T[],
  { files, marks, viewers, cycle, switchesCycle = false }: PdfThreadsOnShow,
  translate: Translate,
): T[] {
  if (!threads.some((thread) => thread.pdfAnnotation)) return [...threads];

  const fileOrder = new Map<string, number>();
  files.forEach(({ path }, index) => fileOrder.has(path) || fileOrder.set(path, index));

  const placed = threads
    .filter((thread): thread is T & { pdfAnnotation: PdfAnnotationRef } => !!thread.pdfAnnotation)
    .map((thread) => {
      const ref = thread.pdfAnnotation;
      const cycleOnShow = viewers.get(ref.file) ?? cycle;
      const otherCycle = ref.cycle != null && cycleOnShow != null && ref.cycle !== cycleOnShow;
      const mark = ref.removed || otherCycle ? undefined : markOf(marks, ref);
      const notOnShow = !ref.removed && (otherCycle ? !switchesCycle : !viewers.has(ref.file));
      const textBox = mark?.type === PdfAnnotationSubtype.FREETEXT;
      const fileName = files.find(({ path }) => path === ref.file)?.fileName ?? ref.file.split('/').pop() ?? ref.file;
      const position = [fileOrder.get(ref.file) ?? files.length, ref.removed ? 1 : 0, ref.pageIndex ?? 0, mark?.rect?.origin.y ?? 0];
      return {
        thread: {
          ...thread,
          label: threadLabel(ref, fileName, mark, otherCycle, translate),
          ...((notOnShow || textBox) && { pdfAnnotation: { ...ref, ...(notOnShow && { notOnShow }), ...(textBox && { textBox }) } }),
        },
        position,
      };
    })
    .sort((a, b) => a.position.reduce((order, value, i) => order || value - b.position[i], 0));

  return [...threads.filter((thread) => !thread.pdfAnnotation), ...placed.map(({ thread }) => thread)];
}

function markOf(marks: PdfThreadsOnShow['marks'], { file, annotationId }: PdfAnnotationRef): CommentedAnnotation | undefined {
  return (marks[file] as CommentedAnnotation[] | undefined)?.find(({ id }) => id === annotationId);
}

function threadLabel(
  ref: PdfAnnotationRef,
  fileName: string,
  mark: Pick<CommentedAnnotation, 'type'> | undefined,
  otherCycle: boolean,
  translate: Translate,
): string {
  const typeKey = mark && TYPE_KEYS[mark.type];
  const where =
    ref.removed || ref.pageIndex == null
      ? [translate(REMOVED_KEY)]
      : [translate(PAGE_KEY, { page: ref.pageIndex + 1 }), typeKey && translate(typeKey)];
  return [fileName, ...where, otherCycle && translate(CYCLE_KEY, { cycle: ref.cycle })].filter(Boolean).join(PDF_THREAD_LABEL_SEPARATOR);
}

/** A note replying to a mark, as the comment pane writes one. A group's members point at their leader too, and aren't. */
export function isReply<T extends object>(annotation: T): annotation is T & { inReplyToId: string } {
  const { inReplyToId, replyType, type } = annotation as Partial<CommentedAnnotation>;
  return !!inReplyToId && replyType !== PdfAnnotationReplyType.Group && type === PdfAnnotationSubtype.TEXT;
}

function hasText(annotation: CommentedAnnotation): boolean {
  return !!annotation.contents?.trim();
}

function byCreated(a: CommentedAnnotation, b: CommentedAnnotation): number {
  return createdTime(a) - createdTime(b);
}

function createdTime(annotation: CommentedAnnotation): number {
  return new Date(annotation.created ?? annotation.modified ?? 0).getTime() || 0;
}

function toComment(annotation: CommentedAnnotation, draft: boolean): FeedbackCommentItem {
  const created = annotation.created ?? annotation.modified;
  return {
    id: annotation.id,
    authorId: null,
    authorName: annotation.author || null,
    // A reviewer may be a tutor or a peer, and the annotation doesn't say which.
    authorRole: null,
    body: annotation.contents?.trim() ?? '',
    createdAt: created instanceof Date ? created.toISOString() : (created ?? ''),
    edited: false,
    parentCommentId: null,
    ...(draft && { draft }),
  };
}
